import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import DataTable from "react-data-table-component";
import apiClient from "../api/apiClient";
import Select from "react-select";
import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts?.default?.vfs || pdfFonts.vfs;

// ── Constantes ──────────────────────────────────────────────
const SERVICE_TYPES = [
  { value: "DM", label: "Desayuno/Merienda" },
  { value: "AC", label: "Almuerzo/Cena" },
  { value: "LIM", label: "Limpieza" },
  { value: "UTE", label: "Útiles" },
  { value: "EQU", label: "Equipamiento" },
];

// Tolerancia del 0.5 % para productos congelados (carne, pollo, etc.)
// Se aplica a ítems cuya unidad de medida es por peso (KG, Kg, kg, etc.)
const TOLERANCIA_CONGELADOS = 0.005; // 0.5 %

/** Devuelve true si la unidad de medida indica peso → tolerancia de congelados */
const esUnidadPeso = (um) => {
  if (!um) return false;
  const u = String(um).trim().toUpperCase();
  return ["KG", "KGS", "GR", "GRS", "LT", "LTS"].includes(u);
};

// ── Clasificación por Rubro (Etiquetas de canasta) ──────────
const RUBROS = {
  VE: "Vegetales",
  RE: "Refrigerados",
  NP: "No Perecederos",
  UT: "Utensilios",
  LI: "Limpieza",
  OT: "Otros",
};

/** Determina el rubro de un ítem según su servicio o grupo */
const getRubro = (servicio) => {
  if (!servicio) return "OT";
  const s = String(servicio).trim().toUpperCase();
  if (s === "AC" || s === "DM") return "VE"; // Alimentos → Vegetales por defecto
  if (s === "LIM") return "LI";
  if (s === "UTE") return "UT";
  if (s === "EQU") return "OT";
  return "OT";
};

// Capacidad máxima por tipo de canasta (kg)
const CANASTA_CAPACIDAD = { A: 25, B: 15 };

/**
 * Empaqueta las líneas preparadas de una OV en canastas respetando el peso máximo.
 * Cada canasta agrupa ítems hasta completar la capacidad.
 * Retorna un array de canastas, cada una con sus ítems y peso total.
 */
const empaquetarCanastas = (lines, prepared, tipoCanasta) => {
  const maxPeso = CANASTA_CAPACIDAD[tipoCanasta] || 25;
  const canastas = [];
  let canastaActual = { items: [], pesoTotal: 0 };

  // Ordenar líneas por rubro para agrupar mejor
  const lineasConPeso = lines
    .map((ln) => {
      const key = `${ln.DocEntry}-${ln.LineNum}`;
      const cantPrep = parseFloat(prepared[key]) || 0;
      if (cantPrep <= 0) return null;
      const peso = esUnidadPeso(ln.SalUnitMsr) ? cantPrep : 0;
      return { ...ln, cantPrep, pesoItem: peso };
    })
    .filter(Boolean);

  for (const item of lineasConPeso) {
    let pesoRestante = item.pesoItem;
    let cantRestante = item.cantPrep;

    while (cantRestante > 0) {
      const espacioDisponible = maxPeso - canastaActual.pesoTotal;

      if (pesoRestante > 0 && pesoRestante > espacioDisponible && espacioDisponible < maxPeso * 0.05 && canastaActual.items.length > 0) {
        // Canasta casi llena, cerrar y abrir nueva
        canastas.push(canastaActual);
        canastaActual = { items: [], pesoTotal: 0 };
        continue;
      }

      if (pesoRestante > 0 && pesoRestante > espacioDisponible && canastaActual.items.length > 0) {
        // Dividir el ítem entre canastas
        const fraccion = espacioDisponible / pesoRestante;
        const cantEnEsta = parseFloat((cantRestante * fraccion).toFixed(3));
        const pesoEnEsta = parseFloat(espacioDisponible.toFixed(3));
        canastaActual.items.push({ ...item, cantPrep: cantEnEsta, pesoItem: pesoEnEsta });
        canastaActual.pesoTotal += pesoEnEsta;
        cantRestante -= cantEnEsta;
        pesoRestante -= pesoEnEsta;
        canastas.push(canastaActual);
        canastaActual = { items: [], pesoTotal: 0 };
      } else {
        // Cabe completo en la canasta actual
        canastaActual.items.push({ ...item, cantPrep: cantRestante, pesoItem: pesoRestante });
        canastaActual.pesoTotal += pesoRestante;
        cantRestante = 0;
        pesoRestante = 0;
      }
    }
  }

  if (canastaActual.items.length > 0) {
    canastas.push(canastaActual);
  }

  return canastas;
};

/**
 * Genera el contenido PDF de las etiquetas de canasta para una OV.
 */
const generarEtiquetasPDF = (ov, canastas, config) => {
  const { operador, codOperador, tipoCanasta, ordenInicio, rubro: rubroManual } = config;
  const totalCanastas = canastas.length;
  const rubro = rubroManual || getRubro(ov.Orders?.[0]?.U_Serv);
  const rubroLabel = RUBROS[rubro] || "Otros";
  const today = new Date();
  const fechaHoy = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()}`;

  const pages = canastas.map((canasta, idx) => {
    const numCanasta = idx + 1;
    const orden = (parseInt(ordenInicio) || 1) + idx;
    const tipo = canasta.items.length === 1 ? "FULL" : "MIXTO";
    const pesoAprox = canasta.pesoTotal > 0 ? `${canasta.pesoTotal.toFixed(1)} kg` : "—";

    const pageContent = [
      // Título
      {
        text: "ETIQUETADO DE CANASTA (CAJA DE PLÁSTICO)",
        bold: true,
        fontSize: 12,
        alignment: "center",
        color: "#c00000",
        margin: [0, 0, 0, 8],
      },
      // Fila principal con datos
      {
        columns: [
          {
            width: "65%",
            stack: [
              // OT y Fecha
              {
                table: {
                  widths: [35, 120, 35, 70],
                  body: [
                    [
                      { text: "OT", bold: true, fontSize: 9 },
                      { text: `${ov.DocNum} - ${ov.Ruta || ""}`, fontSize: 9 },
                      { text: "Fecha", bold: true, fontSize: 9 },
                      { text: fechaHoy, fontSize: 9 },
                    ],
                  ],
                },
                layout: "lightHorizontalLines",
                margin: [0, 0, 0, 4],
              },
              // Institución, Ciudad, Código
              {
                table: {
                  widths: [170, 50, 60],
                  body: [
                    [
                      { text: ov.Institucion || "—", bold: true, fontSize: 10 },
                      { text: "PYTO", fontSize: 8, alignment: "center" },
                      { text: ov.Project || "", fontSize: 8, alignment: "center" },
                    ],
                  ],
                },
                layout: "lightHorizontalLines",
                margin: [0, 0, 0, 4],
              },
              // Ruta (de la OV, no el rango)
              {
                table: {
                  widths: [50, 60],
                  body: [
                    [
                      { text: "RUTA", bold: true, fontSize: 9 },
                      { text: `${ov.Ruta || ""}`, fontSize: 9 },
                    ],
                  ],
                },
                layout: "noBorders",
                margin: [0, 0, 0, 4],
              },
              // Tipo y Orden
              {
                columns: [
                  {
                    width: "auto",
                    table: {
                      widths: [35, 55],
                      body: [
                        [
                          { text: "TIPO", bold: true, fontSize: 8, fillColor: "#f0f0f0" },
                          { text: tipo, bold: true, fontSize: 9, alignment: "center" },
                        ],
                      ],
                    },
                    layout: "lightHorizontalLines",
                  },
                  { width: 10, text: "" },
                  {
                    width: "auto",
                    table: {
                      widths: [45, 55],
                      body: [
                        [
                          { text: "ORDEN", bold: true, fontSize: 8 },
                          { text: `_${orden}/${totalCanastas + (parseInt(ordenInicio) || 1) - 1}`, fontSize: 9, alignment: "center" },
                        ],
                      ],
                    },
                    layout: "lightHorizontalLines",
                  },
                ],
                margin: [0, 0, 0, 4],
              },
              // Operador
              {
                table: {
                  widths: [55, 140, 30],
                  body: [
                    [
                      { text: "Operador", bold: true, fontSize: 8 },
                      { text: operador || "", fontSize: 9 },
                      { text: codOperador || "", fontSize: 9, alignment: "center" },
                    ],
                  ],
                },
                layout: "lightHorizontalLines",
                margin: [0, 0, 0, 4],
              },
            ],
          },
          {
            width: "35%",
            stack: [
              // Tipo de canasta
              {
                table: {
                  widths: ["*"],
                  body: [
                    [{ text: `Cosechera ${tipoCanasta}`, bold: true, fontSize: 11, alignment: "center" }],
                  ],
                },
                margin: [0, 0, 0, 6],
              },
              // Rubro
              {
                table: {
                  widths: [25, "*"],
                  body: [
                    [
                      { text: rubro, bold: true, fontSize: 14, alignment: "center", color: "#006600" },
                      { text: rubroLabel, fontSize: 9 },
                    ],
                  ],
                },
                margin: [0, 0, 0, 6],
              },
              // Fracción
              {
                table: {
                  widths: ["*"],
                  body: [
                    [
                      {
                        text: `_${numCanasta}/${totalCanastas}`,
                        bold: true,
                        fontSize: 14,
                        alignment: "center",
                      },
                    ],
                  ],
                },
                margin: [0, 0, 0, 6],
              },
              // P.B. aprox
              {
                table: {
                  widths: [55, "*"],
                  body: [
                    [
                      { text: "P.B.aprox", bold: true, fontSize: 8 },
                      { text: pesoAprox, fontSize: 10, alignment: "center", bold: true },
                    ],
                  ],
                },
                margin: [0, 0, 0, 4],
              },
            ],
          },
        ],
      },
    ];

    // Salto de página excepto en la última etiqueta
    if (idx < canastas.length - 1) {
      pageContent.push({ text: "", pageBreak: "after" });
    }

    return pageContent;
  });

  return pages.flat();
};

// ── Componente principal ────────────────────────────────────
const Picking = () => {
  // Estado de datos
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Filtros
  const [entDate, setEntDate] = useState("");
  const [serviceType, setServiceType] = useState(null);
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startRoute, setStartRoute] = useState("");
  const [endRoute, setEndRoute] = useState("");

  // Cantidades preparadas por clave `${DocEntry}-${LineNum}`
  const [prepared, setPrepared] = useState({});
  // OVs marcadas como "picking finalizado" por DocEntry
  const [checkedOVs, setCheckedOVs] = useState({});
  // Modal de confirmación: { type: "check"|"deliver", docEntry?, docNum? }
  const [confirmModal, setConfirmModal] = useState(null);
  const expandedScrollTopRef = useRef(0);

  // Artículos alternativos
  const [altModal, setAltModal] = useState(null); // { line, docEntry, alternatives: [] }
  const [altLoading, setAltLoading] = useState(false);
  const [altSubmitting, setAltSubmitting] = useState(false); // mientras se sustituye/revierte en SAP

  // Etiquetas de canasta
  const [etiquetaModal, setEtiquetaModal] = useState(null); // { ov } o null
  const [etiquetaConfig, setEtiquetaConfig] = useState({
    operador: "",
    codOperador: "",
    tipoCanasta: "A",
    ordenInicio: "1",
    rubro: "",
  });

  // ── Cargar proyectos ──────────────────────────────────────
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await apiClient.get("/sales-orders/projects");
        const data = Array.isArray(res.data) ? res.data : [];
        setProjects(data.map((p) => ({ value: p.PrjCode, label: p.PrjName })));
      } catch (e) {
        console.error(e);
        setError("Error al obtener los proyectos.");
      }
    };
    fetchProjects();
  }, []);

  // ── Buscar artículos alternativos ─────────────────────────
  const fetchAlternativos = async (line, docEntry) => {
    setAltLoading(true);
    try {
      const sapUser = JSON.parse(localStorage.getItem("sapUser"));
      const Company = sapUser?.CompanyDB || "";
      const { data } = await apiClient.get("/inventario/alternativos", {
        params: { itemCode: line.ItemCode, whsCode: line.WhsCode, company: Company },
      });
      const arr = Array.isArray(data) ? data : [];
      setAltModal({ line, docEntry, alternatives: arr });
    } catch (e) {
      console.error(e);
      alert("Error al obtener artículos alternativos.");
    } finally {
      setAltLoading(false);
    }
  };

  // ── Sustituir línea por artículo alternativo ──────────────
  const aplicarAlternativo = async (alt) => {
    if (!altModal || altSubmitting) return;
    const { line, docEntry } = altModal;

    setAltSubmitting(true);
    try {
      // 1. Actualizar la OV en SAP primero
      const resp = await apiClient.patch("/inventario/sustituir-item", {
        docEntry: docEntry,
        lineNum: line.LineNum,
        newItemCode: alt.ItemCode,
      });

      if (resp.data?.status !== "success") {
        alert(`Error al sustituir en SAP: ${resp.data?.message || "Error desconocido"}`);
        return;
      }

      // 2. Solo si SAP aceptó, actualizar el estado local
      setOrders((prev) =>
        prev.map((ov) => {
          if (String(ov.DocEntry) !== String(docEntry)) return ov;
          return {
            ...ov,
            Orders: ov.Orders.map((ln) => {
              if (ln.LineNum !== line.LineNum) return ln;
              return {
                ...ln,
                ItemCodeOriginal: ln.ItemCodeOriginal || ln.ItemCode,
                DscriptionOriginal: ln.DscriptionOriginal || ln.Dscription,
                ItemCode: alt.ItemCode,
                Dscription: alt.Dscription || alt.ItemName || alt.ItemCode,
                OnHand: alt.OnHand != null ? alt.OnHand : ln.OnHand,
                SalUnitMsr: alt.SalUnitMsr || alt.InvntryUom || ln.SalUnitMsr,
                sustituido: true,
              };
            }),
          };
        })
      );

      // Aplicar la cantidad ajustada según U_AjusteNeto del sustituto
      // (ej: 100 KG - 16% = 84 KG). Queda editable.
      const ajuste = parseFloat(alt.AjusteNeto) || 0;
      const cantOriginal = parseFloat(line.OpenQty ?? line.Quantity ?? 0) || 0;
      const cantAjustada = Number((cantOriginal * (1 - ajuste / 100)).toFixed(6));
      const prepKey = `${docEntry}-${line.LineNum}`;
      setPrepared((prev) => ({ ...prev, [prepKey]: cantAjustada }));

      setAltModal(null);
    } catch (e) {
      console.error("Error al sustituir artículo en SAP:", e);
      const msg = e?.response?.data?.message || e?.response?.data?.error || "Error de conexión";
      alert(`Error al sustituir artículo en la OV de SAP:\n${msg}`);
    } finally {
      setAltSubmitting(false);
    }
  };

  // ── Revertir sustitución ──────────────────────────────────
  const revertirAlternativo = async (line, docEntry) => {
    if (!line.ItemCodeOriginal) return;

    try {
      // 1. Revertir en SAP primero
      const resp = await apiClient.patch("/inventario/revertir-item", {
        docEntry: docEntry,
        lineNum: line.LineNum,
        originalItemCode: line.ItemCodeOriginal,
      });

      if (resp.data?.status !== "success") {
        alert(`Error al revertir en SAP: ${resp.data?.message || "Error desconocido"}`);
        return;
      }

      // 2. Solo si SAP aceptó, actualizar el estado local
      setOrders((prev) =>
        prev.map((ov) => {
          if (String(ov.DocEntry) !== String(docEntry)) return ov;
          return {
            ...ov,
            Orders: ov.Orders.map((ln) => {
              if (ln.LineNum !== line.LineNum || !ln.ItemCodeOriginal) return ln;
              return {
                ...ln,
                ItemCode: ln.ItemCodeOriginal,
                Dscription: ln.DscriptionOriginal,
                ItemCodeOriginal: undefined,
                DscriptionOriginal: undefined,
                sustituido: false,
              };
            }),
          };
        })
      );

      // Restaurar la cantidad a preparar a la cantidad original (descarta el ajuste)
      const cantOriginal = parseFloat(line.OpenQty ?? line.Quantity ?? 0) || 0;
      const prepKey = `${docEntry}-${line.LineNum}`;
      setPrepared((prev) => ({ ...prev, [prepKey]: cantOriginal }));
    } catch (e) {
      console.error("Error al revertir artículo en SAP:", e);
      const msg = e?.response?.data?.message || e?.response?.data?.error || "Error de conexión";
      alert(`Error al revertir artículo en la OV de SAP:\n${msg}`);
    }
  };

  // ── Abrir modal de etiquetas para una OV ──────────────────
  const abrirEtiquetaModal = (ov) => {
    const rubroDetectado = getRubro(ov.Orders?.[0]?.U_Serv);
    setEtiquetaConfig((prev) => ({ ...prev, rubro: rubroDetectado }));
    setEtiquetaModal({ ov });
  };

  // ── Generar PDF de etiquetas para la OV seleccionada ──────
  const handleGenerarEtiquetas = () => {
    if (!etiquetaModal) return;
    const { ov } = etiquetaModal;
    const canastas = empaquetarCanastas(ov.Orders, prepared, etiquetaConfig.tipoCanasta);

    if (canastas.length === 0) {
      alert("No hay cantidades preparadas para generar etiquetas en esta OV.");
      return;
    }

    // Sobreescribir el rubro si el usuario eligió uno manualmente
    const configConRubro = { ...etiquetaConfig };
    const content = generarEtiquetasPDF(ov, canastas, configConRubro);

    pdfMake.createPdf({
      content,
      pageSize: "A5",
      pageOrientation: "landscape",
      pageMargins: [20, 15, 20, 15],
      defaultStyle: { fontSize: 9 },
    }).open();

    setEtiquetaModal(null);
  };

  // ── Generar etiquetas para TODAS las OVs finalizadas ──────
  const handleGenerarTodasEtiquetas = () => {
    const ovsFinalizadas = orders.filter((ov) => checkedOVs[ov.DocEntry]);
    if (ovsFinalizadas.length === 0) {
      alert("Debe marcar al menos una OV como 'Picking Finalizado' antes de generar etiquetas.");
      return;
    }

    let allPages = [];
    let ordenGlobal = parseInt(etiquetaConfig.ordenInicio) || 1;

    ovsFinalizadas.forEach((ov, ovIdx) => {
      const canastas = empaquetarCanastas(ov.Orders, prepared, etiquetaConfig.tipoCanasta);
      if (canastas.length === 0) return;

      const configOV = { ...etiquetaConfig, ordenInicio: String(ordenGlobal) };
      const pages = generarEtiquetasPDF(ov, canastas, configOV);
      ordenGlobal += canastas.length;

      if (allPages.length > 0) {
        allPages.push({ text: "", pageBreak: "after" });
      }
      allPages.push(...pages);
    });

    if (allPages.length === 0) {
      alert("No hay cantidades preparadas en las OVs finalizadas.");
      return;
    }

    pdfMake.createPdf({
      content: allPages,
      pageSize: "A5",
      pageOrientation: "landscape",
      pageMargins: [20, 15, 20, 15],
      defaultStyle: { fontSize: 9 },
    }).open();
  };

  // ── Obtener datos de consolidado (agrupados por OV) ──────
  const fetchInsumos = async () => {
    setLoading(true);
    setError("");
    const sapUser = JSON.parse(localStorage.getItem("sapUser"));
    const Company = sapUser?.CompanyDB || "";
    try {
      const { data } = await apiClient.get("/planif/consolidado/preparacion", {
        params: {
          project: project?.value ?? "",
          servicio: serviceType?.value ?? "",
          start_route: startRoute,
          end_route: endRoute,
          entDate,
          company: Company,
        },
      });

      const arr = Array.isArray(data) ? data : [];
      if (!arr.length) {
        setOrders([]);
        setPrepared({});
        setCheckedOVs({});
        return;
      }

      // Agrupar por DocEntry (cada OV es un grupo)
      const grouped = arr.reduce((acc, order) => {
        const key = `${order.DocEntry}`;
        if (!acc[key]) {
          acc[key] = {
            DocEntry: order.DocEntry,
            DocNum: order.DocNum,
            Project: order.Project,
            Institucion: order.Name,
            CardCode: order.CardCode,
            FechaEntrega: order.DocDueDate,
            Ruta: order.U_Ruteo || "",
            RutaIni: startRoute,
            RutaFin: endRoute,
            Orders: [],
          };
        }
        acc[key].Orders.push(order);
        return acc;
      }, {});

      const formattedData = Object.values(grouped);

      // Inicializar prepared: por defecto OpenQty (toda la cantidad pendiente)
      const initialPrepared = {};
      formattedData.forEach((ov) => {
        ov.Orders.forEach((ln) => {
          const key = `${ln.DocEntry}-${ln.LineNum}`;
          const openQty = parseFloat(ln.OpenQty ?? ln.Quantity ?? 0) || 0;
          initialPrepared[key] = openQty;
        });
      });

      setOrders(formattedData);
      setPrepared(initialPrepared);
      setCheckedOVs({});
    } catch (e) {
      console.error(e);
      setError("Error al obtener órdenes de ventas.");
    } finally {
      setLoading(false);
    }
  };

  // ── Filtro de búsqueda ────────────────────────────────────
  const filteredOrders = useMemo(() => {
    if (!searchTerm) return orders;
    const q = searchTerm.toLowerCase();
    return orders.filter(
      (ov) =>
        (ov.Institucion || "").toLowerCase().includes(q) ||
        (ov.Project || "").toLowerCase().includes(q) ||
        String(ov.DocNum || "").includes(q)
    );
  }, [orders, searchTerm]);

  // ── Componente expandible por OV ──────────────────────────
  const ExpandedComponent = ({ data }) => {
    /** Maneja el cambio de cantidad a preparar con tolerancia para congelados */
    const handlePreparedChange = (line, value) => {
      // Si la UM es RAC, no permitir modificación
      const umUpper = String(line.SalUnitMsr || "").trim().toUpperCase();
      if (umUpper === "RAC") return;

      const key = `${line.DocEntry}-${line.LineNum}`;
      const openQty = parseFloat(line.OpenQty ?? line.Quantity ?? 0) || 0;
      let val = parseFloat(value);
      if (isNaN(val)) val = 0;
      if (val < 0) val = 0;

      // Tolerancia: si la UM es de peso → permitir 0.5 % más (producto congelado)
      const maxQty = esUnidadPeso(line.SalUnitMsr)
        ? openQty * (1 + TOLERANCIA_CONGELADOS)
        : openQty;

      if (val > maxQty) val = parseFloat(maxQty.toFixed(6));

      setPrepared((prev) => ({ ...prev, [key]: val }));
    };

    // Total preparado de esta OV
    const preparedTotalOV = data.Orders.reduce((sum, ln) => {
      const key = `${ln.DocEntry}-${ln.LineNum}`;
      return sum + (parseFloat(prepared[key]) || 0);
    }, 0);

    // Scroll persistente
    const scrollRef = useRef(null);
    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const onScroll = () => { expandedScrollTopRef.current = el.scrollTop; };
      el.addEventListener("scroll", onScroll, { passive: true });
      return () => el.removeEventListener("scroll", onScroll);
    }, []);
    useLayoutEffect(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = expandedScrollTopRef.current;
    }, [prepared]);

    return (
      <div className="p-2" style={{ width: "100%" }}>
        {/* ── Picking por línea ── */}
        <h6 style={{ fontSize: 14 }}>Picking por línea (OV #{data.DocNum})</h6>
        <small className="text-muted d-block mb-2">
          * Los ítems con unidad de peso (KG) permiten un <strong>0,5 %</strong> adicional por variación de producto congelado.
        </small>

        <div
          ref={scrollRef}
          style={{ overflowX: "auto", overflowY: "auto", width: "100%", maxHeight: "70vh" }}
        >
          <div style={{ minWidth: 1150 }}>
            <table className="table table-sm table-bordered" style={{ fontSize: 13, tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                <col style={{ width: 40 }} />
                <col style={{ width: 75 }} />
                <col />
                <col style={{ width: 55 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 35 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 70 }} />
                <col style={{ width: 50 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Línea</th>
                  <th>ItemCode</th>
                  <th>Descripción</th>
                  <th>UM</th>
                  <th style={{ textAlign: "right" }}>Cant. Pedida</th>
                  <th style={{ textAlign: "right" }}>Cant. Pend.</th>
                  <th style={{ textAlign: "right" }}>Cant. Prep.</th>
                  <th style={{ textAlign: "center" }}>Tol.</th>
                  <th style={{ textAlign: "right" }}>Stock</th>
                  <th>Depósito</th>
                  <th style={{ textAlign: "center" }}>Alt.</th>
                </tr>
              </thead>
              <tbody>
                {data.Orders.map((ln) => {
                  const key = `${ln.DocEntry}-${ln.LineNum}`;
                  const qty = parseFloat(ln.Quantity ?? 0) || 0;
                  const openQty = parseFloat(ln.OpenQty ?? qty) || 0;
                  const umUpper = String(ln.SalUnitMsr || "").trim().toUpperCase();
                  const esRAC = umUpper === "RAC";
                  const tieneTol = !esRAC && esUnidadPeso(ln.SalUnitMsr);
                  const maxQty = tieneTol
                    ? openQty * (1 + TOLERANCIA_CONGELADOS)
                    : openQty;
                  const stockItem = ln.OnHand != null ? parseFloat(ln.OnHand) || 0 : null;
                  const stockInsuficiente = stockItem !== null && stockItem < openQty;

                  return (
                    <tr key={key} style={ln.sustituido ? { background: "#fff8e1" } : undefined}>
                      <td>{ln.LineNum}</td>
                      <td style={{ whiteSpace: "normal", wordBreak: "break-word", overflow: "hidden" }}>
                        {ln.ItemCode}
                        {ln.sustituido && (
                          <div style={{ fontSize: 10, color: "#888" }}>
                            (orig: {ln.ItemCodeOriginal})
                          </div>
                        )}
                      </td>
                      <td style={{ whiteSpace: "normal", wordBreak: "break-word", overflow: "hidden" }}>{ln.Dscription}</td>
                      <td>{ln.SalUnitMsr}</td>
                      <td style={{ textAlign: "right" }}>{qty.toLocaleString("es-ES")}</td>
                      <td style={{ textAlign: "right" }}>{openQty.toLocaleString("es-ES")}</td>
                      <td style={{ textAlign: "right" }}>
                        {esRAC ? (
                          <input
                            type="number"
                            className="form-control form-control-sm text-end"
                            value={prepared[key] ?? 0}
                            readOnly
                            disabled
                            style={{ background: "#e9ecef", width: "100%" }}
                          />
                        ) : (
                          <input
                            type="number"
                            className="form-control form-control-sm text-end"
                            min={0}
                            max={parseFloat(maxQty.toFixed(6))}
                            step="any"
                            value={prepared[key] ?? 0}
                            onChange={(e) => handlePreparedChange(ln, e.target.value)}
                            style={{ width: "100%" }}
                          />
                        )}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {esRAC ? (
                          <span className="badge bg-secondary" title="Ración: cantidad fija">—</span>
                        ) : tieneTol ? (
                          <span className="badge bg-info text-dark" title="Producto congelado: +0,5 % tolerancia">
                            +0,5%
                          </span>
                        ) : (
                          <span className="badge bg-secondary">—</span>
                        )}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {stockItem !== null ? (
                          <span className={stockItem <= 0 ? "text-danger fw-bold" : ""}>
                            {stockItem.toLocaleString("es-ES")}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>{ln.WhsCode || ""}</td>
                      <td style={{ textAlign: "center" }}>
                        {esRAC ? null : ln.sustituido ? (
                          <button
                            className="btn btn-outline-secondary btn-sm p-0"
                            style={{ fontSize: 11, lineHeight: 1, width: 28, height: 24 }}
                            title="Revertir al artículo original"
                            onClick={() => revertirAlternativo(ln, data.DocEntry)}
                          >
                            ↩
                          </button>
                        ) : stockInsuficiente ? (
                          <button
                            className="btn btn-outline-warning btn-sm p-0"
                            style={{ fontSize: 11, lineHeight: 1, width: 28, height: 24 }}
                            title="Buscar artículo alternativo"
                            onClick={() => fetchAlternativos(ln, data.DocEntry)}
                            disabled={altLoading}
                          >
                            🔄
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6}><strong>Total a preparar (OV #{data.DocNum})</strong></td>
                  <td style={{ textAlign: "right" }}><strong>{preparedTotalOV.toLocaleString("es-ES")}</strong></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* ── Check de picking finalizado por OV ── */}
        <div className="d-flex align-items-center justify-content-between mt-2 p-2 rounded"
          style={{
            background: checkedOVs[data.DocEntry] ? "#d4edda" : "#fff3cd",
            border: checkedOVs[data.DocEntry] ? "1px solid #28a745" : "1px solid #ffc107",
          }}
        >
          <div className="d-flex align-items-center">
            <input
              type="checkbox"
              className="form-check-input me-2"
              id={`check-ov-${data.DocEntry}`}
              checked={!!checkedOVs[data.DocEntry]}
              onChange={() => {
                if (checkedOVs[data.DocEntry]) {
                  setCheckedOVs((prev) => ({ ...prev, [data.DocEntry]: false }));
                } else {
                  setConfirmModal({ type: "check", docEntry: data.DocEntry, docNum: data.DocNum });
                }
              }}
              style={{ width: 20, height: 20 }}
            />
            <label className="form-check-label fw-bold" htmlFor={`check-ov-${data.DocEntry}`} style={{ cursor: "pointer" }}>
              {checkedOVs[data.DocEntry]
                ? `✅ Picking finalizado (OV #${data.DocNum})`
                : `Marcar picking como finalizado (OV #${data.DocNum})`}
            </label>
          </div>
          <button
            className="btn btn-sm btn-outline-info"
            onClick={() => abrirEtiquetaModal(data)}
            title="Generar etiquetas de canasta para esta OV"
          >
            🏷️ Etiquetas
          </button>
        </div>
      </div>
    );
  };

  // ── Exportar PDF ──────────────────────────────────────────
  const exportarPDF = () => {
    const datos = orders.flatMap((o) => o.Orders);

    const grouped = datos.reduce((acc, item) => {
      const key = `${item.ItemCode}|${item.Dscription}|${item.SalUnitMsr}`;
      if (!acc[key]) {
        acc[key] = { ItemCode: item.ItemCode, Dscription: item.Dscription, SalUnitMsr: item.SalUnitMsr, Quantity: 0 };
      }
      acc[key].Quantity += parseFloat(item.Quantity) || 0;
      return acc;
    }, {});
    const rows = Object.values(grouped);

    let checklistItems = [];
    orders.forEach((o) => {
      o.Orders.forEach((line) => {
        const key = `${line.DocNum}|${line.Name}|${line.U_Serv}`;
        if (!checklistItems.some((i) => `${i.DocNum}|${i.Name}|${i.U_Serv}` === key)) {
          checklistItems.push({ DocNum: line.DocNum, Name: line.Name, U_Serv: line.U_Serv });
        }
      });
    });
    checklistItems.sort((a, b) =>
      a.U_Serv === b.U_Serv ? a.DocNum.toString().localeCompare(b.DocNum.toString()) : a.U_Serv.localeCompare(b.U_Serv)
    );

    const content = [
      { text: "PICKING — PRODUCTOS CONSOLIDADOS", bold: true, fontSize: 16, margin: [0, 0, 0, 10] },
      { text: `Institución: ${orders[0]?.Institucion || ""}`, fontSize: 12 },
      { text: `Ruta: ${startRoute} — ${endRoute}`, fontSize: 12 },
      { text: `Fecha de Entrega: ${entDate}`, fontSize: 12 },
      { text: "\n" },
      {
        table: {
          headerRows: 1,
          widths: ["auto", "*", "auto", "auto", "auto", "auto"],
          body: [
            [
              { text: "Producto", bold: true },
              { text: "Descripción", bold: true },
              { text: "UM", bold: true },
              { text: "Cantidad Total", bold: true },
              { text: "Nro. de Lote", bold: true },
              { text: "Peso Total", bold: true },
            ],
            ...rows.map((r) => [r.ItemCode, r.Dscription, r.SalUnitMsr, r.Quantity.toLocaleString("es-ES"), entDate, ""]),
          ],
        },
        fontSize: 11,
      },
      {
        text: "Checklist de Entrega por Escuela",
        bold: true,
        fontSize: 11,
        margin: [0, 10, 0, 4],
        pageBreak: "before",
      },
      {
        table: {
          headerRows: 1,
          widths: ["auto", "*", "auto", "auto"],
          body: [
            [
              { text: "DocNum", bold: true },
              { text: "Escuela", bold: true },
              { text: "Servicio", bold: true },
              { text: "Check", bold: true },
            ],
            ...checklistItems.map((item) => [item.DocNum, item.Name, item.U_Serv, ""]),
          ],
        },
        fontSize: 12,
      },
    ];

    pdfMake.createPdf({ content, pageOrientation: "portrait", defaultStyle: { fontSize: 8 } }).open();
  };

  // ── Confirmar y generar entregas ──────────────────────────
  /** Abre el modal de confirmación final antes de generar entregas */
  const requestConfirmAndDeliver = () => {
    // Verificar que al menos 1 OV esté checkeada
    const checkedCount = orders.filter((ov) => checkedOVs[ov.DocEntry]).length;
    if (checkedCount === 0) {
      alert("Debe marcar al menos una OV como 'Picking Finalizado' antes de generar entregas.");
      return;
    }
    setConfirmModal({ type: "deliver" });
  };

  const handleConfirmAndDeliver = async () => {
    setConfirmModal(null);
    try {
      setSubmitting(true);
      setError("");

      const toISODate = (d) => {
        if (!d) return "";
        const dt = new Date(d);
        return isNaN(dt) ? String(d).slice(0, 10) : dt.toISOString().slice(0, 10);
      };
      const todayISO = new Date().toISOString().slice(0, 10);

      const deliveries = orders
        .filter((ov) => checkedOVs[ov.DocEntry]) // solo OVs marcadas como finalizadas
        .map((ov) => {
          const lines = ov.Orders.map((ln) => {
            const key = `${ln.DocEntry}-${ln.LineNum}`;
            const qty = parseFloat(prepared[key]) || 0;
            if (qty <= 0) return null;
            return {
              BaseType: 17,
              BaseEntry: ln.DocEntry,
              BaseLine: ln.LineNum,
              ItemCode: ln.ItemCode,
              Quantity: qty,
              UnitPrice: ln.AvgPrice,
              WarehouseCode: ln.WhsCode || ln.WarehouseCode || "",
              TaxCode: ln.TaxCode || ln.VatGroup || "",
            };
          }).filter(Boolean);

          if (!lines.length) return null;

          return {
            DocDate: todayISO,
            DocDueDate: toISODate(ov.FechaEntrega || entDate),
            CardCode: ov.CardCode || ov.Orders?.[0]?.CardCode || "",
            ContactPersonCode: ov.Orders?.[0]?.Institucion ?? ov.Orders?.[0]?.CntctCode ?? null,
            U_Enviar: "NO",
            DocumentLines: lines,
          };
        })
        .filter(Boolean);

      if (!deliveries.length) {
        alert("No hay cantidades preparadas para generar entregas.");
        return;
      }

      const resp = await apiClient.post("/inventario/entregas", { orders: deliveries });
      const results = resp?.data || [];
      const ok = results.filter((r) => r.status === "success").length;
      const bad = results.filter((r) => r.status === "error").length;

      let msg = "";
      if (ok) msg += `${ok} entregas creadas con éxito.\n`;
      if (bad) msg += `${bad} entregas con error.\n`;
      if (!msg) msg = "Proceso finalizado.";
      alert(msg);

      await fetchInsumos();
    } catch (err) {
      console.error("Error generando entregas", err);
      setError("Ocurrió un error al generar las entregas.");
      alert("Ocurrió un error al generar las entregas.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Columnas de la tabla principal ────────────────────────
  const columns = [
    { name: "OV #", selector: (row) => row.DocNum, sortable: true, width: "100px" },
    { name: "Proyecto", selector: (row) => row.Project, sortable: true, width: "180px" },
    { name: "Institución", selector: (row) => row.Institucion, sortable: true, width: "280px" },
    { name: "Ruta", selector: (row) => row.Ruta, sortable: true, width: "90px" },
    { name: "Fecha Entrega", selector: (row) => row.FechaEntrega, sortable: true, width: "140px" },
    {
      name: "Estado",
      width: "130px",
      center: true,
      cell: (row) =>
        checkedOVs[row.DocEntry] ? (
          <span className="badge bg-success">✅ Finalizado</span>
        ) : (
          <span className="badge bg-warning text-dark">⏳ Pendiente</span>
        ),
    },
  ];

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="container mt-4">
      <h2>Picking</h2>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row">
        <div className="col-md-3">
          <label>Tipo de Servicio:</label>
          <Select
            options={SERVICE_TYPES}
            value={serviceType}
            onChange={setServiceType}
            placeholder="Seleccione un Servicio..."
            isSearchable
            isClearable
          />
        </div>

        <div className="col-md-4">
          <label>Código de Proyecto:</label>
          <Select
            options={projects}
            value={project}
            onChange={setProject}
            placeholder="Seleccione un proyecto..."
            isSearchable
            isClearable
          />
        </div>

        <div className="col-md-2">
          <label>Fecha de Entrega:</label>
          <input
            type="date"
            className="form-control"
            value={entDate}
            onChange={(e) => setEntDate(e.target.value)}
          />
        </div>

        <div className="col-md-1">
          <label>Ruta Ini:</label>
          <input
            type="number"
            className="form-control"
            value={startRoute}
            onChange={(e) => setStartRoute(e.target.value)}
          />
        </div>

        <div className="col-md-1">
          <label>Ruta Fin:</label>
          <input
            type="number"
            className="form-control"
            value={endRoute}
            onChange={(e) => setEndRoute(e.target.value)}
          />
        </div>
      </div>

      <div className="d-flex gap-2 mt-3 flex-wrap">
        <button className="btn btn-success" onClick={fetchInsumos} disabled={loading}>
          {loading ? "Cargando..." : "Obtener Picking"}
        </button>

        <button className="btn btn-primary" onClick={exportarPDF} disabled={loading || !orders.length}>
          Imprimir PDF
        </button>

        <button
          className="btn btn-warning"
          onClick={() => {
            const reset = {};
            orders.forEach((ov) => {
              ov.Orders.forEach((ln) => {
                const key = `${ln.DocEntry}-${ln.LineNum}`;
                reset[key] = 0;
              });
            });
            setPrepared(reset);
            setCheckedOVs({});
          }}
          disabled={!orders.length || submitting}
        >
          Limpiar Cantidades
        </button>

        <button className="btn btn-dark" onClick={requestConfirmAndDeliver} disabled={!orders.length || submitting}>
          {submitting ? "Generando Entregas..." : "Confirmar y Generar Entregas"}
        </button>

        <button className="btn btn-info" onClick={handleGenerarTodasEtiquetas} disabled={!orders.length}>
          🏷️ Etiquetas (Todas)
        </button>

        <div className="ms-auto" style={{ minWidth: 220 }}>
          <input
            type="text"
            className="form-control"
            placeholder="Buscar (Institución / Proyecto / OV)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <DataTable
        className="mt-3"
        columns={columns}
        data={filteredOrders}
        expandableRows
        expandableRowsComponent={ExpandedComponent}
        expandableRowExpanded={() => true}
        pagination
        highlightOnHover
        dense
        noDataComponent="No hay datos para mostrar. Utilice los filtros y presione 'Obtener Picking'."
      />

      {/* ── Modal de confirmación ── */}
      {confirmModal && (
        <>
          <div className="modal-backdrop show" style={{ zIndex: 1040 }}></div>
          <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {confirmModal.type === "check"
                      ? "Confirmar Picking Finalizado"
                      : "Confirmar Generación de Entregas"}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setConfirmModal(null)}></button>
                </div>
                <div className="modal-body">
                  {confirmModal.type === "check" ? (
                    <p>
                      ¿Está seguro de que finalizó el picking de la{" "}
                      <strong>OV #{confirmModal.docNum}</strong>?
                      <br />
                      <small className="text-muted">
                        Una vez marcada, quedará indicada como completada.
                      </small>
                    </p>
                  ) : (
                    <div>
                      <p>
                        <strong>¿Está seguro de que desea generar las entregas?</strong>
                      </p>
                      <p>
                        Se generarán entregas para{" "}
                        <strong>
                          {orders.filter((ov) => checkedOVs[ov.DocEntry]).length} OV(s)
                        </strong>{" "}
                        marcadas como finalizadas.
                      </p>
                      <p className="text-danger mb-0">
                        <small>Esta acción no se puede deshacer.</small>
                      </p>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setConfirmModal(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className={`btn ${confirmModal.type === "check" ? "btn-success" : "btn-dark"}`}
                    onClick={() => {
                      if (confirmModal.type === "check") {
                        setCheckedOVs((prev) => ({ ...prev, [confirmModal.docEntry]: true }));
                        setConfirmModal(null);
                      } else {
                        handleConfirmAndDeliver();
                      }
                    }}
                  >
                    {confirmModal.type === "check" ? "Sí, finalizado" : "Sí, generar entregas"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Modal de artículos alternativos ── */}
      {altModal && (
        <>
          <div className="modal-backdrop show" style={{ zIndex: 1060 }}></div>
          <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1070 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Artículos Alternativos para <strong>{altModal.line.ItemCode}</strong>
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setAltModal(null)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-2">
                    <strong>Artículo original:</strong> {altModal.line.ItemCode} — {altModal.line.Dscription}
                    <br />
                    <strong>Depósito:</strong> {altModal.line.WhsCode || "—"}
                    {" | "}
                    <strong>Stock actual:</strong>{" "}
                    <span className="text-danger fw-bold">
                      {altModal.line.OnHand != null ? parseFloat(altModal.line.OnHand).toLocaleString("es-ES") : "—"}
                    </span>
                    {" | "}
                    <strong>Cant. pendiente:</strong> {parseFloat(altModal.line.OpenQty ?? altModal.line.Quantity ?? 0).toLocaleString("es-ES")}
                  </p>
                  {altModal.alternatives.length === 0 ? (
                    <div className="alert alert-warning mb-0">
                      No se encontraron artículos alternativos para este producto.
                    </div>
                  ) : (
                    <table className="table table-sm table-hover table-bordered" style={{ fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th>ItemCode</th>
                          <th>Descripción</th>
                          <th>UM</th>
                          <th style={{ textAlign: "right" }}>Stock</th>
                          <th style={{ textAlign: "right" }}>Ajuste %</th>
                          <th style={{ textAlign: "right" }}>Cant. ajustada</th>
                          <th style={{ textAlign: "center" }}>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {altModal.alternatives.map((alt, idx) => {
                          const altStock = alt.OnHand != null ? parseFloat(alt.OnHand) || 0 : 0;
                          const ajuste = parseFloat(alt.AjusteNeto) || 0;
                          const cantOriginal = parseFloat(altModal.line.OpenQty ?? altModal.line.Quantity ?? 0) || 0;
                          const cantAjustada = Number((cantOriginal * (1 - ajuste / 100)).toFixed(6));
                          return (
                            <tr key={idx}>
                              <td>{alt.ItemCode}</td>
                              <td>{alt.Dscription || alt.ItemName || ""}</td>
                              <td>{alt.SalUnitMsr || alt.InvntryUom || ""}</td>
                              <td style={{ textAlign: "right" }}>
                                <span className={altStock <= 0 ? "text-danger fw-bold" : "text-success fw-bold"}>
                                  {altStock.toLocaleString("es-ES")}
                                </span>
                              </td>
                              <td style={{ textAlign: "right" }}>
                                {ajuste.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %
                              </td>
                              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                                {cantAjustada.toLocaleString("es-ES")}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => aplicarAlternativo(alt)}
                                  disabled={altStock <= 0 || altSubmitting}
                                  title={altStock <= 0 ? "Sin stock disponible" : "Sustituir por este artículo"}
                                >
                                  {altSubmitting ? "Guardando..." : "Sustituir"}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setAltModal(null)}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Modal de configuración de etiquetas de canasta ── */}
      {etiquetaModal && (
        <>
          <div className="modal-backdrop show" style={{ zIndex: 1080 }}></div>
          <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1090 }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header" style={{ background: "#e3f2fd" }}>
                  <h5 className="modal-title">
                    🏷️ Generar Etiquetas — OV #{etiquetaModal.ov.DocNum}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setEtiquetaModal(null)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    <strong>Institución:</strong> {etiquetaModal.ov.Institucion}
                    <br />
                    <strong>Proyecto:</strong> {etiquetaModal.ov.Project}
                    {" | "}
                    <strong>Ruta:</strong> {etiquetaModal.ov.Ruta || "—"}
                  </p>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Tipo de Canasta</label>
                      <select
                        className="form-select"
                        value={etiquetaConfig.tipoCanasta}
                        onChange={(e) => setEtiquetaConfig((prev) => ({ ...prev, tipoCanasta: e.target.value }))}
                      >
                        <option value="A">Cosechera A (hasta 25 kg)</option>
                        <option value="B">Cosechera B (hasta 15 kg)</option>
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">Rubro</label>
                      <select
                        className="form-select"
                        value={etiquetaConfig.rubro}
                        onChange={(e) => setEtiquetaConfig((prev) => ({ ...prev, rubro: e.target.value }))}
                      >
                        {Object.entries(RUBROS).map(([k, v]) => (
                          <option key={k} value={k}>{k} — {v}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">Nombre del Operador</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: Carlos Gomez"
                        value={etiquetaConfig.operador}
                        onChange={(e) => setEtiquetaConfig((prev) => ({ ...prev, operador: e.target.value }))}
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label fw-bold">Cód. Operador</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: 12"
                        value={etiquetaConfig.codOperador}
                        onChange={(e) => setEtiquetaConfig((prev) => ({ ...prev, codOperador: e.target.value }))}
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label fw-bold">Orden Inicio</label>
                      <input
                        type="number"
                        className="form-control"
                        min={1}
                        value={etiquetaConfig.ordenInicio}
                        onChange={(e) => setEtiquetaConfig((prev) => ({ ...prev, ordenInicio: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Vista previa: cuántas canastas se generarán */}
                  {(() => {
                    const canastas = empaquetarCanastas(
                      etiquetaModal.ov.Orders,
                      prepared,
                      etiquetaConfig.tipoCanasta
                    );
                    return (
                      <div className="alert alert-info mt-3 mb-0" style={{ fontSize: 13 }}>
                        <strong>Vista previa:</strong> Se generarán{" "}
                        <strong>{canastas.length}</strong> etiqueta(s) de canasta
                        {" "}(Cosechera {etiquetaConfig.tipoCanasta} — máx. {CANASTA_CAPACIDAD[etiquetaConfig.tipoCanasta]} kg)
                        {canastas.length > 0 && (
                          <ul className="mb-0 mt-1">
                            {canastas.map((c, i) => (
                              <li key={i}>
                                Canasta {i + 1}: {c.items.length} ítem(s)
                                {c.pesoTotal > 0 ? ` — ${c.pesoTotal.toFixed(1)} kg` : ""}
                                {" — "}
                                {c.items.length === 1 ? "FULL" : "MIXTO"}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setEtiquetaModal(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-info"
                    onClick={handleGenerarEtiquetas}
                  >
                    🏷️ Generar PDF de Etiquetas
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Picking;
